import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';

const DomainsSection = ({ onUpdate, boostInput = false, fieldName = 'domains' }) => {
  const { fetchCachedList, selectedIndex } = useBuild();
  const [currentDomain, setCurrentDomain] = useState('');
  const [boost, setBoost] = useState(1);
  const [domainOptions, setDomainOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch domain options on component mount and when selectedIndex changes
  useEffect(() => {
    const fetchDomainOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const endpoint = `/search/frequent_values/${selectedIndex}/${fieldName}`;
        const domainsData = await fetchCachedList(endpoint);
        setDomainOptions(domainsData);
      } catch (err) {
        console.error('Failed to fetch domain options:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDomainOptions();
  }, [fetchCachedList, selectedIndex, fieldName]);

  const handleDomainAction = (actionType) => {
    if (!currentDomain) return;
    
    const updateData = {
      action: actionType,
      filter: 'terms',
      field: fieldName,
      value: [currentDomain]
    };
    
    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }
    
    onUpdate(updateData);
    setCurrentDomain(''); // Reset after adding
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">
          Failed to load domains: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Domain</InputLabel>
        <Select
          value={currentDomain}
          label="Domain"
          onChange={(e) => setCurrentDomain(e.target.value)}
          disabled={loading}
        >
          {domainOptions.map((domain) => (
            <MenuItem key={domain.id} value={domain.id}>
              {domain.id} ({domain.count})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          disabled={loading}
        />
      )}
      
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleDomainAction('include')}
          disabled={!currentDomain || loading}
        >
          <AddIcon />
        </Button>
        
        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleDomainAction('boost')}
            disabled={!currentDomain || loading}
          >
            <TrendingUpIcon />
          </Button>
        )}
        
        <Button
          variant="contained"
          color="error"
          onClick={() => handleDomainAction('exclude')}
          disabled={!currentDomain || loading}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default DomainsSection;
